import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import Button from '@/components/common/Button'
import toast from 'react-hot-toast'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp } from 'react-icons/fa'

interface ContactFormData {
  name: string
  email: string
  phone?: string
  subject: string
  message: string
}

const Contact = () => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>()

  const onSubmit = (data: ContactFormData) => {
    console.log(data)
    toast.success(t('contact.messageSent'))
    reset()
  }

  const contactCards = [
    {
      icon: FaPhone,
      label: t('contact.phone'),
      value: '+49 (0) 551 49203480',
      href: 'tel:+4955149203480',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      icon: FaWhatsapp,
      label: t('contact.whatsapp'),
      value: '+49 155 65553294',
      href: 'https://wa.me/4915565553294',
      gradient: 'from-green-500 to-green-600',
      external: true,
    },
    {
      icon: FaEnvelope,
      label: t('contact.email'),
      value: 'info@kennzeichenpro24.de',
      href: 'mailto:info@kennzeichenpro24.de',
      gradient: 'from-primary-500 to-primary-600',
    },
    {
      icon: FaMapMarkerAlt,
      label: t('contact.address'),
      value: 'Zollstock 15, 37081 Göttingen-Grone',
      href: 'https://www.google.com/maps/place/KennzeichenPro24+GmbH+-+Kennzeichen+%26+Zulassungsdienst/@51.5327381,9.8954551,17z/data=!3m1!4b1!4m6!3m5!1s0x47bb2bb3eb63a731:0xef60c60b7ec04863!8m2!3d51.5327348!4d9.89803!16s%2Fg%2F11yt76_s7c',
      gradient: 'from-red-500 to-red-600',
      external: true,
    },
  ]

  return (
    <div className="bg-dark-950 min-h-screen py-12 relative overflow-hidden">
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-gradient">{t('contact.title')}</span>
            </h1>
            <p className="text-gray-400 max-w-xl mx-auto">{t('contact.subtitle')}</p>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto mt-4"></div>
          </motion.div>

          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-12 w-full h-[300px] sm:h-[400px] rounded-2xl overflow-hidden shadow-2xl border border-gray-800"
          >
            <iframe
              src="https://maps.google.com/maps?q=KennzeichenPro24+GmbH,+Zollstock+15,+37081+G%C3%B6ttingen-Grone&hl=de&z=16&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="grayscale hover:grayscale-0 transition-all duration-700 rounded-2xl"
            ></iframe>
          </motion.div>

          {/* Contact Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {contactCards.map((card, index) => (
              <motion.a
                key={card.label}
                href={card.href}
                target={card.external ? '_blank' : undefined}
                rel={card.external ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1, duration: 0.5 }}
                whileHover={{ y: -4, scale: 1.02 }}
                className="card-hover p-5 text-center group"
              >
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${card.gradient} text-white mb-3 shadow-lg group-hover:scale-110 transition-transform`}>
                  <card.icon className="w-5 h-5" />
                </div>
                <div className="text-xs text-gray-500 mb-1">{card.label}</div>
                <div className="text-sm text-gray-200 font-medium">{card.value}</div>
              </motion.a>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Info + Hours + Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="space-y-6"
            >
              {/* Opening Hours */}
              <div className="card-hover">
                <h2 className="text-xl font-bold mb-4 text-gradient flex items-center gap-2">
                  <FaClock className="w-5 h-5 text-primary-400" />
                  {t('contact.workingHours')}
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-dark-700/50">
                    <span className="text-gray-400">Montag – Freitag</span>
                    <span className="text-gray-200 font-semibold">09:00 – 15:30</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-dark-700/50">
                    <span className="text-gray-400">Samstag</span>
                    <span className="text-gray-200 font-semibold">10:00 – 14:00</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-400">Sonntag</span>
                    <span className="text-red-400 font-semibold">Geschlossen</span>
                  </div>
                </div>
              </div>

              {/* Visit Us */}
              <div className="card-hover">
                <h2 className="text-xl font-bold mb-4 text-gradient flex items-center gap-2">
                  <FaMapMarkerAlt className="w-5 h-5 text-primary-400" />
                  {t('contact.visitUs')}
                </h2>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {t('contact.visitUsDesc')}
                </p>
                <div className="text-gray-200 font-medium">
                  <p>KennzeichenPro24 GmbH</p>
                  <p className="text-gray-400">Zollstock 15</p>
                  <p className="text-gray-400">37081 Göttingen-Grone</p>
                  <p className="text-gray-400">Niedersachsen, Deutschland</p>
                </div>
                <a
                  href="https://www.google.com/maps/place/KennzeichenPro24+GmbH+-+Kennzeichen+%26+Zulassungsdienst/@51.5327381,9.8954551,17z/data=!3m1!4b1!4m6!3m5!1s0x47bb2bb3eb63a731:0xef60c60b7ec04863!8m2!3d51.5327348!4d9.89803!16s%2Fg%2F11yt76_s7c"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary-500/10 text-primary-400 rounded-lg hover:bg-primary-500/20 transition-colors text-sm font-medium"
                >
                  <FaMapMarkerAlt className="w-3.5 h-3.5" />
                  Auf Google Maps anzeigen
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="card-hover"
            >
              <h2 className="text-2xl font-bold mb-6 text-gradient">{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.name')} *
                  </label>
                  <input
                    {...register('name', { required: t('contact.nameRequired') })}
                    type="text"
                    className="input-field"
                  />
                  {errors.name && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.email')} *
                  </label>
                  <input
                    {...register('email', {
                      required: t('contact.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('contact.emailInvalid'),
                      },
                    })}
                    type="email"
                    className="input-field"
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.phone')}
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.subject')} *
                  </label>
                  <input
                    {...register('subject', { required: t('contact.subjectRequired') })}
                    type="text"
                    className="input-field"
                  />
                  {errors.subject && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.message')} *
                  </label>
                  <textarea
                    {...register('message', { required: t('contact.messageRequired') })}
                    rows={4}
                    className="input-field resize-none"
                  />
                  {errors.message && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.message.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full">
                  {t('contact.send')}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
